import { BaseTable, Button, Card, HeaderMenu, Input, Loading, Pagination, Select } from "components"
import { Delete, SaveFile } from "./components"
import { DeleteContext, UploadContext } from "./contexts"
import { LuFileSearch } from "react-icons/lu"
import moment from "moment"
import { Link } from "react-router-dom"
import { handleFileDownload, statusList, useFileList, useHandleFile, useList } from "./utils"

export default function AccountImport(): JSX.Element {
  const { ref, selectedFile, handleFileCancel, handleFileChange, handleSaveFileExcel } = useHandleFile()
  const { data, filter, isLoading, pagination, handleDropdownChange, handlePaginationChange, refetch } = useList()

  return (
    <section className="container pt-5  gap-5 flex flex-col">
      <HeaderMenu title="ACCOUNT IMPORT" />

      <Card>
        <Card.Body className="flex-row justify-between items-center">
          <div className="flex items-center gap-3">
            <Input
              className="w-[300px] cursor-pointer"
              placeholder="Choose file"
              value={selectedFile?.name ? selectedFile?.name : ""}
            />

            <Input 
              type="file" 
              ref={ref}
              className="w-[300px] absolute opacity-0 cursor-pointer"
              accept=".xlsx, .xls"
              onChange={handleFileChange}
            />

            <UploadContext.Provider
              value={{
                handleSaveFileExcel,
                onSuccess: () => {
                  handleFileCancel()
                  refetch()
                },
              }}
            >
              <SaveFile disabled={!selectedFile} />
            </UploadContext.Provider>
          </div>

          <Button
            color="primary"
            onClick={handleFileDownload}
          >
            FORMAT DOWNLOAD
          </Button>
        </Card.Body>

        <Card.Body className="flex-row justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3">
              <div className="text-nowrap font-bold">FILE NAME</div>

              <div className="w-[200px]">
                <FileDropdown
                  value={filter.file_name}
                  onChange={res => handleDropdownChange("file_name", res)}
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-nowrap font-bold">STATUS</div>

              <div className="w-[200px]">
                <Select
                  options={statusList}
                  value={statusList.find((item) => item.value === filter.file_status)}
                  onChange={(item) => {
                    if (item) {
                      handleDropdownChange("file_status", item.value)
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </Card.Body>

        <Card.Body>
          <BaseTable>
            <thead>
              <tr>
                <th>NO</th>
                <th>DATE</th>
                <th>FILE NAME</th>
                <th>DESCRIPTION</th>
                <th>STATUS</th>
                <th className="w-[150px]">ACTION</th>
              </tr>
            </thead>
            <tbody>
              {isLoading || data.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center">
                    <Loading loading={isLoading} errorText="No data available" />
                  </td>
                </tr>
              ) : data.map((item, key) => {
                // Vars
                const status = statusList.find(i => i.value === item.status.toString())
                const isReview: boolean = status?.label === "Review"

                return (
                  <tr key={key}>
                    <td className="text-center">{key + 1}</td>
                    <td>{moment(item.transaction_date).format("DD/MM/YYYY")}</td>
                    <td>{item.file_name}</td>
                    <td>{item.file_description}</td>
                    <td>{status?.label}</td>
                    <td>
                      <div className="flex items-center justify-center gap-3">
                        {isReview && (
                          <DeleteContext.Provider
                            value={{
                              refetch,
                              data: item,
                            }}
                          >
                            <Delete />
                          </DeleteContext.Provider>
                        )}

                        <Link
                          to="/setup/accounts/account-import/review"
                          state={item}
                        >
                          <LuFileSearch className="h-[20px] w-[20px] cursor-pointer" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </BaseTable>

          <div className="mt-5">
            <Pagination
              currentPage={pagination.page}
              totalPage={pagination.pages}
              onPageChange={res => handlePaginationChange("page", res)}
              onAddShow={(res) => handlePaginationChange("limit", parseInt(res))}
              limit={filter.limit}
              total={pagination.total}
              size={pagination.size}
            />
          </div>
        </Card.Body>
      </Card>
    </section>
  )
}

function FileDropdown(params: {
  value: string
  onChange: (value: string) => void
}): JSX.Element {
  // Hooks
  const { data, isLoading } = useFileList()

  return (
    <Select
      isLoading={isLoading}
      options={data}
      value={data.find((item) => item.value === params.value)}
      onChange={(item) => {
        if (item) {
          params.onChange(item.value)
        }
      }}
    />
  )
}